import React, { useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

const DatePicker = ({ onChange, value, inlineStyles }) => {
  const renderMonth = (month) => {
    const isCurrentMonth = moment().month() === month.month();
    const maxDate = moment().add(180, 'days');
    const firstDay = moment(month).startOf('month').format('d');

    const blanks = [];

    for (let i = 0; i < firstDay; i += 1) {
      const blanksKey = `blanks-${i}`;
      blanks.push(
        <div key={blanksKey} className="w-8 h-8 m-1 rounded-full xs:h-10 xs:w-10 xs:m-2" />,
      );
    }
    const daysInMonth = [];
    for (let d = 1; d <= month.daysInMonth(); d += 1) {
      const isPastDate = maxDate.isBefore(`${month.year()}-${`${month.month() + 1}`.padStart(2, '0')}-${`${d}`.padStart(2, '0')}`);
      const isSelectedDate = `${month.year()}-${d}-${month.month()}` === `${moment(value).year()}-${moment(value).date()}-${moment(value).month()}`;
      daysInMonth.push(
        <div
          key={d}
          className={classnames('cursor-pointer rounded-full h-8 xs:h-10 w-8 xs:w-10 m-1 xs:m-2 flex items-center justify-center font-primary text-3.5 -tracking-0.2 text-dark', {
            'border border-dark': isCurrentMonth && moment().date() === d,
            'text-light-grey-3 line-through pointer-events-none': (isCurrentMonth && moment().date() > d) || isPastDate,
            'bg-dark text-white': isSelectedDate,
          })}
          onClick={() => {
            onChange(moment(`${month.year()}-${`${month.month() + 1}`.padStart(2, '0')}-${`${d}`.padStart(2, '0')}`, 'YYYY-MM-DD').toDate());
          }}
          aria-hidden
        >
          {d}
        </div>,
      );
    }
    const totalSlots = [...blanks, ...daysInMonth];
    const rows = [];
    let cells = [];
    totalSlots.forEach((row, i) => {
      if (i % 7 !== 0) {
        cells.push(row);
      } else {
        rows.push(cells);
        cells = [];
        cells.push(row);
      }
      if (i === totalSlots.length - 1) {
        if (cells.length < 7) {
          for (let j = cells.length; j < 7; j += 1) {
            const blockKey = `date-picker-block-${j}`;
            cells.push(<div key={blockKey} className="w-8 h-8 m-1 rounded-full xs:h-10 xs:w-10 xs:m-2" />);
          }
        }
        rows.push(cells);
      }
    });
    return rows.map((d, index) => {
      const rowsKey = `date-row-${index}`;
      return <div key={rowsKey} className="flex justify-evenly">{d}</div>;
    });
  };

  const monthsToDisplay = useMemo(() => {
    const months = [];
    for (let index = 0; index < 8; index += 1) {
      months.push(moment().add(index, 'M'));
    }
    return months;
  }, []);

  const displayMonths = (
    <div
      className="px-4 pb-4 overflow-auto"
      style={{
        maxHeight: inlineStyles?.maxHeight || 'calc(100vh - 292px)',
        ...inlineStyles,
      }}
    >
      {monthsToDisplay.map((month, index) => {
        const monthKey = `date-picker-month-${month}-${index}`;
        return (
          <div key={monthKey}>
            <div
              className={classnames('font-primary mb-4 font-bold text-4 leading-6 -tracking-0.3 text-dark', {
                'pt-6': index === 0,
                'pt-8': index !== 0,
              })}
            >
              {month.format('MMMM YYYY')}
            </div>
            {renderMonth(month)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="flex items-center px-4 h-15 bg-white-two justify-evenly">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => {
          const dayKey = `date-picker-${day}-${i}`;
          return (
            <span key={dayKey} className="flex items-center justify-center w-10 h-10 mx-2 font-black leading-none text-black font-primary text-3 -tracking-0.2">
              {day}
            </span>
          );
        })}
      </div>
      {displayMonths}
    </div>
  );
};

DatePicker.defaultProps = {
  value: null,
  inlineStyles: null,
};

DatePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.instanceOf(Date),
  inlineStyles: PropTypes.shape({
    maxHeight: PropTypes.string,
  }),
};

export default DatePicker;
